<template>
  <div class="page-view">

    <BasePageHeader :title="$t('returnView.title')" @back="$router.back()" />

    <BaseTaskHelp task="returns" class="mx-4 mt-3" />

    <!-- Manual return warning -->
    <div v-if="showManualWarning" class="flex items-start gap-3 bg-yellow-950/40 border border-yellow-800 rounded-xl p-3.5 mx-4 mt-3 text-yellow-200 text-sm leading-snug">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-5 h-5 shrink-0 mt-0.5">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
      <div>
      <strong>{{ $t('returnView.manualWarningStrong') }}</strong> {{ $t('returnView.manualWarningBody') }}
      </div>
      <button type="button" :aria-label="$t('common.dismiss')" class="ml-auto bg-transparent border-0 text-yellow-400 cursor-pointer text-xl shrink-0" @click="showManualWarning = false">×</button>
    </div>

    <!-- Scan area -->
    <div class="px-4 pt-3.5 pb-2">
      <SearchField
        ref="scanner"
        :scan-mode="true"
        :camera-mode="true"
        :error="scanError"
        :loading="scanLoading"
        :placeholder="$t('returnView.scanPlaceholder')"
        @scan="onScan"
      />

      <!-- Manual return toggle -->
      <button type="button" :disabled="submitting || returnSubmitting" :aria-pressed="manualMode" class="mt-2 bg-transparent border-0 text-blue-400 text-sm cursor-pointer underline p-0" @click="toggleManualMode">
        {{ manualMode ? $t('returnView.exitManualMode') : $t('returnView.manualReturn') }}
      </button>
    </div>

    <!-- Resolved item + condition prompt -->
    <div v-if="resolvedItem && !showMaintenance" class="px-4">
      <div class="data-card">
        <div class="data-card__name text-xl">{{ resolvedItem.display_name ?? resolvedItem.item?.name ?? resolvedItem.asset_tag ?? resolvedItem.serial_number ?? `#${resolvedItem.id}` }}</div>
        <div class="text-sm text-gray-400 mb-2" v-if="resolvedItem.project">
          {{ $t('returnView.returningFromProject') }} <strong>{{ resolvedItem.project.name }}</strong>
        </div>
        <div class="flex gap-2 flex-wrap mb-3">
          <span v-if="resolvedItem.serial_number" class="badge badge-muted">SN: {{ resolvedItem.serial_number }}</span>
          <span v-if="resolvedItem.asset_tag" class="badge badge-muted">{{ $t('ui.assetTagLabel') }} {{ resolvedItem.asset_tag }}</span>
          <span v-if="isConsumable" class="badge badge-accent">{{ $t('itemTypes.4') }}</span>
          <span v-else-if="isQuantityItem" class="badge badge-muted">{{ $t('itemTypes.3') }}</span>
        </div>

        <!-- Consumable hint: return is optional -->
        <div v-if="isConsumable" class="flex items-start gap-2 mb-4 px-3 py-2.5 rounded-lg bg-amber-900/20 border border-amber-700/30 text-xs text-amber-300">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0 mt-0.5">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>{{ $t('returnView.consumableHint') }}</span>
        </div>

        <!-- Quantity returned — open shared QuantityModal for touch-safe entry -->
        <BaseFormField
          v-if="isConsumable || isQuantityItem"
          :label="$t('returnView.quantityReturned')"
          :required="!isConsumable"
          :error="quantityError || ''"
          class="mb-4"
        >
          <button
            type="button"
            class="btn secondary w-full justify-between"
            :disabled="returnSubmitting"
            @click="showQtyModal = true"
          >
            <span>{{ quantityReturned || 0 }}</span>
            <span class="text-xs opacity-70">{{ $t('ux.returnQuantityHint', { count: resolvedItem._checkout_quantity ?? resolvedItem.quantity ?? 1 }) }}</span>
          </button>
        </BaseFormField>

        <!-- Big GOOD / DAMAGED buttons -->
        <div class="grid gap-3.5" :class="isConsumable ? 'grid-cols-1' : 'grid-cols-2'">
          <button
            v-if="isConsumable"
            class="flex flex-col items-center justify-center gap-2.5 py-5 px-4 rounded-xl text-lg font-black tracking-wide cursor-pointer transition-all active:scale-95 bg-green-950 text-green-300 hover:brightness-110 border-0"
            :disabled="returnSubmitting"
            @click="markGood"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="w-7 h-7">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            {{ $t('returnView.returnRemainingStock') }}
          </button>
          <template v-else>
            <button
              class="flex flex-col items-center justify-center gap-2.5 py-6 px-4 rounded-xl text-xl font-black tracking-wide cursor-pointer transition-all active:scale-95 bg-green-950 text-green-300 hover:brightness-110 border-0"
              :disabled="returnSubmitting"
              @click="markGood"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="w-8 h-8">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              {{ $t('returnView.good') }}
            </button>
            <button
              class="flex flex-col items-center justify-center gap-2.5 py-6 px-4 rounded-xl text-xl font-black tracking-wide cursor-pointer transition-all active:scale-95 bg-red-950 text-red-300 hover:brightness-110 border-0"
              :disabled="returnSubmitting"
              @click="markDamaged"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="w-8 h-8">
                <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
              </svg>
              {{ $t('returnView.damaged') }}
            </button>
          </template>
        </div>
      </div>
    </div>

    <EmptyState v-if="returnError" :message="returnError" variant="error" />
    <p v-if="returnSubmitting" role="status" class="px-4 text-sm text-[var(--color-muted)]">{{ $t('common.working') }}</p>

    <!-- Maintenance form -->
    <MaintenanceForm
      v-if="showMaintenance && resolvedItem"
      :item="resolvedItem"
      @submitted="onMaintenanceSubmitted"
      @cancel="showMaintenance = false"
    />

    <!-- Manual mode: list of active checkouts to mark returned -->
    <div v-if="manualMode && !resolvedItem && !showMaintenance" class="px-4 py-3">
      <EmptyState v-if="manualLoading" :message="$t('returnView.loadingCheckouts')" />
      <EmptyState v-else-if="manualLoadError" :message="manualLoadError" variant="error" :action-label="$t('ux.retry')" @action="loadActiveCheckouts()" />
      <EmptyState v-else-if="!activeCheckouts.length" :message="$t('returnView.noActiveCheckouts')" />
      <template v-else>
        <div class="section-label mb-2.5">
          {{ $t('returnView.activeCheckoutsLabel') }}
        </div>
        <div
          v-for="co in activeCheckouts"
          :key="co.id"
          class="data-card cursor-pointer mb-1.5"
          :class="{ 'ring-2 ring-[var(--color-accent)]': selectedManual.has(co.id) }"
          @click="!submitting && toggleManualSelect(co.id)"
        >
          <BaseFormField density="compact" class="!mb-0 shrink-0" @click.stop>
            <BaseCheckbox
              :model-value="selectedManual.has(co.id)"
              :disabled="submitting"
              :aria-label="co.item_metadata?.item?.name ?? `#${co.id}`"
              @update:model-value="toggleManualSelect(co.id)"
            />
          </BaseFormField>
          <span class="data-card__main">
            <span class="data-card__name block">{{ co.item_metadata?.item?.name ?? `#${co.id}` }}</span>
            <span class="data-card__meta block">{{ co.item_metadata?.asset_tag ?? co.item_metadata?.assetTag ?? co.item_metadata?.serial_number }} · {{ co.project ?? co.user }}</span>
          </span>
        </div>
        <button v-if="manualHasNext" type="button" class="btn ghost mt-2" :disabled="submitting" @click="loadActiveCheckouts(true)">{{ $t('ux.loadMore') }}</button>
        <EmptyState v-if="manualSubmitError" :message="manualSubmitError" variant="error" />

        <button
          class="w-full mt-3.5 py-3.5 bg-yellow-950/40 border border-yellow-800 rounded-xl text-yellow-200 text-base font-bold cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-colors hover:enabled:bg-yellow-950/60"
          :disabled="!selectedManual.size || submitting"
          @click="submitManualReturn"
        >
          {{ submitting ? $t('common.working') : $t('ux.markReturned', { count: selectedManual.size }) }}
        </button>
      </template>
    </div>

    <!-- Return history (recent) -->
    <div v-if="recent.length && !resolvedItem && !showMaintenance" class="px-4 py-3 mt-2">
      <div class="section-label mb-2">{{ $t('returnView.justReturned') }}</div>
      <div v-for="r in recent" :key="r.id" class="flex items-center justify-between py-2 border-b border-gray-800 text-sm">
        <div class="text-gray-300">{{ r.item?.name ?? r.barcode }}</div>
        <span :class="r.condition === 'good' ? 'badge badge-success' : 'badge badge-danger'">
          {{ r.condition === 'good' ? $t('returnView.goodBadge') : $t('returnView.damagedBadge') }}
        </span>
      </div>
    </div>

    <QuantityModal
      v-if="showQtyModal && resolvedItem"
      :item="{ item: resolvedItem.item, planned_quantity: resolvedItem._checkout_quantity ?? resolvedItem.quantity ?? 1, packed_quantity: resolvedItem._checkout_quantity ?? resolvedItem.quantity ?? 1 }"
      mode="unpack"
      :initial-value="quantityReturned"
      @confirm="onQtyConfirm"
      @cancel="showQtyModal = false"
    />

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useToast } from '@/composables/useToast'
import SearchField from '@/components/search/SearchField.vue'
import QuantityModal from '@/components/picking/QuantityModal.vue'
import MaintenanceForm from '@/components/returns/MaintenanceForm.vue'
import { resolveReturn, postReturn, getActiveCheckouts } from '@/api/api.js'
import { useOfflineOps } from '@/composables/useOfflineOps.js'
import { usePermissions } from '@/composables/usePermissions.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseTaskHelp from '@/components/base/BaseTaskHelp.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import EmptyState from '@/components/base/EmptyState.vue'

const toast = useToast()
const route = useRoute()
const { t } = useI18n()
const { queuedPostReturn } = useOfflineOps()
const { isOperatorOrAbove } = usePermissions()
const showQtyModal = ref(false)

function onQtyConfirm({ quantity }) {
  quantityReturned.value = quantity
  showQtyModal.value = false
}

// ── Scan ─────────────────────────────────────────────────────────────────────
const scanner       = ref(null)
const scanError     = ref(null)
const scanLoading   = ref(false)
const resolvedItem  = ref(null)
const showMaintenance = ref(false)
const quantityReturned = ref(0)
const returnSubmitting = ref(false)
const returnError = ref('')
const quantityError = computed(() => {
  if (!isConsumable.value && !isQuantityItem.value) return ''
  const max = resolvedItem.value?._checkout_quantity ?? resolvedItem.value?.quantity ?? 1
  return Number.isInteger(quantityReturned.value) && quantityReturned.value > 0 && quantityReturned.value <= max
    ? '' : t('ux.returnQuantityError', { max })
})

const isConsumable   = computed(() => resolvedItem.value?.item?.item_type === 4)
const isQuantityItem = computed(() => resolvedItem.value?.item?.item_type === 3)

async function onScan(barcode) {
  if (returnSubmitting.value || submitting.value || scanLoading.value) return
  returnError.value = ''
  scanError.value  = null
  scanLoading.value = true
  resolvedItem.value = null
  showMaintenance.value = false
  quantityReturned.value = 0

  try {
    const res = await resolveReturn(barcode)
    resolvedItem.value = res.item_metadata ?? null
    if (!resolvedItem.value) {
      scanError.value = t('ui.checkoutNotFound', { barcode })
    } else {
      // Pre-fill quantity from checkout quantity if available
      const checkoutQty = resolvedItem.value._checkout_quantity ?? resolvedItem.value.quantity ?? null
      quantityReturned.value = checkoutQty ?? 1
    }
  } catch (e) {
    scanError.value = e?.data?.error ?? t('ui.lookupFailedCheckConnection')
  } finally {
    scanLoading.value = false
  }
}

// ── Condition ─────────────────────────────────────────────────────────────────
async function markGood() {
  await submitReturn('good')
}

function markDamaged() {
  if (returnSubmitting.value || quantityError.value) return
  showMaintenance.value = true
}

async function submitReturn(condition) {
  if (returnSubmitting.value || !resolvedItem.value || quantityError.value) return
  returnSubmitting.value = true
  returnError.value = ''
  const returnData = {
    item_metadata_id: resolvedItem.value.id,
    _checkout_id:     resolvedItem.value._checkout_id ?? null,
    condition,
  }
  if (isConsumable.value || isQuantityItem.value) {
    returnData.quantity_returned = quantityReturned.value
  }
  try {
    await queuedPostReturn(returnData)
    recent.value.unshift({ id: Date.now(), item: resolvedItem.value?.item, condition })
    if (recent.value.length > 10) recent.value.pop()
    toast.success(t('ui.returnedCondition', { name: resolvedItem.value.item?.name ?? t('common.item'), condition: t(condition === 'good' ? 'returnView.goodBadge' : 'returnView.damagedBadge') }))
    resolvedItem.value = null
    quantityReturned.value = 0
  } catch {
    returnError.value = t('ux.returnFailed')
  } finally {
    returnSubmitting.value = false
  }
  if (!returnError.value) scanner.value?.focus()
}

async function onMaintenanceSubmitted() {
  recent.value.unshift({ id: Date.now(), item: resolvedItem.value?.item, condition: 'damaged' })
  toast.warning(t('ui.maintenanceCreatedFor', { name: resolvedItem.value?.item?.name ?? t('common.item') }))
  resolvedItem.value = null
  showMaintenance.value = false
  scanner.value?.focus()
}

// ── Manual mode ───────────────────────────────────────────────────────────────
const manualMode       = ref(false)
const manualLoading    = ref(false)
const manualLoadError = ref('')
const manualSubmitError = ref('')
const manualPage = ref(0)
const manualHasNext = ref(false)
const showManualWarning = ref(false)
const activeCheckouts  = ref([])
const selectedManual   = ref(new Set())
const submitting       = ref(false)

function toggleManualMode() {
  if (submitting.value || returnSubmitting.value) return
  // Manual return is operator+ only — reduces accidental inventory drift.
  if (!manualMode.value && !isOperatorOrAbove.value) {
    toast.warning(t('ux.manualReturnDenied'))
    return
  }
  manualMode.value = !manualMode.value
  if (manualMode.value) {
    showManualWarning.value = true
    loadActiveCheckouts()
  } else {
    selectedManual.value = new Set()
  }
}

async function loadActiveCheckouts(append = false) {
  if (manualLoading.value) return
  manualLoading.value = true
  manualLoadError.value = ''
  const page = append ? manualPage.value + 1 : 1
  try {
    const res = await getActiveCheckouts({ page })
    const rows = res.results ?? res
    activeCheckouts.value = append ? [...activeCheckouts.value, ...rows] : rows
    if (!append) selectedManual.value = new Set([...selectedManual.value].filter(id => rows.some(co => co.id === id)))
    manualPage.value = page
    manualHasNext.value = Boolean(res.next)
  } catch {
    manualLoadError.value = t('ux.loadReturnsFailed')
  } finally {
    manualLoading.value = false
  }
}

function toggleManualSelect(id) {
  const s = new Set(selectedManual.value)
  if (s.has(id)) s.delete(id)
  else s.add(id)
  selectedManual.value = s
}

async function submitManualReturn() {
  if (submitting.value || !selectedManual.value.size) return
  submitting.value = true
  manualSubmitError.value = ''
  const failed = new Set()
  const succeeded = new Set()
  for (const id of selectedManual.value) {
    try {
      await queuedPostReturn({ _checkout_id: id, condition: 'good' })
      succeeded.add(id)
    } catch {
      failed.add(id)
    }
  }
  selectedManual.value = failed
  activeCheckouts.value = activeCheckouts.value.filter(co => !succeeded.has(co.id))
  if (failed.size) manualSubmitError.value = t('ux.partialReturn', { succeeded: succeeded.size, failed: failed.size })
  else toast.warning(t('ux.manualReturnDone', { count: succeeded.size }))
  submitting.value = false
}

// ── Recent ────────────────────────────────────────────────────────────────────
const recent = ref([])

onMounted(() => {
  const scan = route.query.scan
  if (scan) onScan(String(scan))
})
</script>


